import React, {useState} from 'react';
import {Box, Modal, Typography, IconButton, Tooltip, Button, styled} from '@mui/material';
import {Icon} from '@iconify/react';
import helpCircleOutline from '@iconify/icons-eva/question-mark-circle-outline';
import closeFill from '@iconify/icons-eva/close-fill';
import playCircleOutline from '@iconify/icons-eva/play-circle-outline';
import infoOutline from '@iconify/icons-eva/info-outline';

// Types and interfaces
export type TutorialContentType = 'video' | 'text' | 'image';

export interface VideoContent {
	type: 'video';
	/** YouTube URL or video ID */
	url: string;
	/** Optional title for the video */
	title?: string;
	/** Optional description */
	description?: string;
}

export interface TextContent {
	type: 'text';
	/** Main content text */
	content: string;
	/** Optional title */
	title?: string;
	/** Optional subtitle */
	subtitle?: string;
}

export interface ImageContent {
	type: 'image';
	/** Image URL (supports JPG, PNG, GIF, etc.) */
	imageUrl: string;
	/** Alt text for accessibility */
	alt: string;
	/** Optional caption */
	caption?: string;
	/** Optional title */
	title?: string;
}

export type TutorialContent = VideoContent | TextContent | ImageContent;

export interface TutorialTipProps {
	/** Tutorial content to display */
	content: TutorialContent;
	/** Trigger style - icon or text button */
	triggerType?: 'icon' | 'text';
	/** Custom trigger text (only for text type) */
	triggerText?: string;
	/** Custom icon (only for icon type) */
	triggerIcon?: object;
	/** Tooltip text on hover */
	tooltipText?: string;
	/** Custom styling for trigger */
	triggerSx?: object;
	/** Modal size */
	modalSize?: 'small' | 'medium' | 'large';
	/** Custom modal width (overrides modalSize) */
	modalWidth?: number;
	/** Callback when modal opens */
	onOpen?: () => void;
	/** Callback when modal closes */
	onClose?: () => void;
}

// Styled components
const StyledModal = styled(Modal)(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
}));

const ModalContent = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'size',
})<{size: 'small' | 'medium' | 'large'; customWidth?: number}>(({theme, size, customWidth}) => {
	const widthMap = {
		small: 400,
		medium: 600,
		large: 800,
	};

	return {
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.shape.borderRadius * 2,
		boxShadow: theme.shadows[24],
		padding: theme.spacing(3),
		maxHeight: '90vh',
		overflow: 'auto',
		outline: 'none',
		position: 'relative',
		width: customWidth || widthMap[size],
		maxWidth: '95vw',
	};
});

const CloseButton = styled(IconButton)(({theme}) => ({
	position: 'absolute',
	top: theme.spacing(1),
	right: theme.spacing(1),
	color: theme.palette.grey[500],
}));

const VideoContainer = styled(Box)(({theme}) => ({
	position: 'relative',
	paddingBottom: '56.25%', // 16:9 aspect ratio
	height: 0,
	overflow: 'hidden',
	borderRadius: theme.shape.borderRadius,
	'& iframe': {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
	},
}));

const ImageContainer = styled(Box)(({theme}) => ({
	textAlign: 'center',
	'& img': {
		maxWidth: '100%',
		maxHeight: '60vh',
		borderRadius: theme.shape.borderRadius,
	},
}));

// Utility functions
const extractYouTubeVideoId = (url: string): string | null => {
	// Handle various YouTube URL formats
	const patterns = [
		/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
		/^([a-zA-Z0-9_-]{11})$/, // Direct video ID
	];

	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match) {
			return match[1];
		}
	}

	return null;
};

// Content renderers
const VideoContentRenderer: React.FC<{content: VideoContent}> = ({content}) => {
	if (!content.url) {
		return (
			<Box textAlign="center" py={4}>
				<Typography color="error">Invalid YouTube URL. Please check the URL format.</Typography>
			</Box>
		);
	}

	return (
		<Box>
			{content.title && (
				<Typography variant="h6" gutterBottom>
					{content.title}
				</Typography>
			)}
			{content.description && (
				<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
					{content.description}
				</Typography>
			)}
			<VideoContainer>
				<iframe
					src={content.url}
					title={content.title || 'Tutorial Video'}
					frameBorder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowFullScreen
				/>
			</VideoContainer>
		</Box>
	);
};

const TextContentRenderer: React.FC<{content: TextContent}> = ({content}) => (
	<Box>
		{content.title && (
			<Typography variant="h6" gutterBottom>
				{content.title}
			</Typography>
		)}
		{content.subtitle && (
			<Typography variant="subtitle1" color="text.secondary" gutterBottom>
				{content.subtitle}
			</Typography>
		)}
		<Typography variant="body1" sx={{whiteSpace: 'pre-wrap', lineHeight: 1.6}}>
			{content.content}
		</Typography>
	</Box>
);

const ImageContentRenderer: React.FC<{content: ImageContent}> = ({content}) => (
	<Box>
		{content.title && (
			<Typography variant="h6" gutterBottom textAlign="center">
				{content.title}
			</Typography>
		)}
		<ImageContainer>
			<img src={content.imageUrl} alt={content.alt} />
		</ImageContainer>
		{content.caption && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{mt: 1, display: 'block', textAlign: 'center'}}
			>
				{content.caption}
			</Typography>
		)}
	</Box>
);

// Main component
export const TutorialTip: React.FC<TutorialTipProps> = ({
	content,
	triggerType = 'icon',
	triggerText = 'Help',
	triggerIcon = helpCircleOutline,
	tooltipText,
	triggerSx = {},
	modalSize = 'medium',
	modalWidth,
	onOpen,
	onClose,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleOpen = () => {
		setIsOpen(true);
		onOpen?.();
	};

	const handleClose = () => {
		setIsOpen(false);
		onClose?.();
	};

	// Dynamic tooltip text based on content type
	const defaultTooltipText =
		tooltipText ||
		{
			video: 'Watch tutorial video',
			text: 'View tutorial',
			image: 'View tutorial image',
		}[content.type];

	// Dynamic icon based on content type
	const contentIcon = {
		video: playCircleOutline,
		text: infoOutline,
		image: infoOutline,
	}[content.type];

	const TriggerComponent =
		triggerType === 'icon' ? (
			<Tooltip title={defaultTooltipText}>
				<IconButton
					onClick={handleOpen}
					size="small"
					sx={{
						color: 'primary.main',
						'&:hover': {
							backgroundColor: 'primary.lighter',
						},
						...triggerSx,
					}}
				>
					<Icon icon={triggerIcon || contentIcon} width={20} height={20} />
				</IconButton>
			</Tooltip>
		) : (
			<Button
				onClick={handleOpen}
				size="small"
				startIcon={<Icon icon={contentIcon} />}
				sx={{
					textTransform: 'none',
					...triggerSx,
				}}
			>
				{triggerText}
			</Button>
		);

	const renderContent = () => {
		switch (content.type) {
			case 'video':
				return <VideoContentRenderer content={content} />;
			case 'text':
				return <TextContentRenderer content={content} />;
			case 'image':
				return <ImageContentRenderer content={content} />;
			default:
				return <Typography>Unsupported content type</Typography>;
		}
	};

	return (
		<>
			{TriggerComponent}
			<StyledModal
				open={isOpen}
				onClose={handleClose}
				aria-labelledby="tutorial-modal-title"
				aria-describedby="tutorial-modal-description"
			>
				<ModalContent size={modalSize} customWidth={modalWidth}>
					<CloseButton onClick={handleClose} aria-label="Close tutorial">
						<Icon icon={closeFill} />
					</CloseButton>
					{renderContent()}
				</ModalContent>
			</StyledModal>
		</>
	);
};

export default TutorialTip;
